import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Text,
  useToast,
  Box,
  IconButton
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { KMDeckTheme } from '../types/KMDeck';

// Default theme values
const defaultTheme: KMDeckTheme = {
  pageBackground: '#171923', // gray.900
  buttonEdges: '#333333', // original empty button border
  emptyButtonBackground: '#1a1a1a', // original empty button background
  glowEffect: '#ffa500', // original glow color
  glowDisabled: false,
};

interface ColorCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (theme: KMDeckTheme) => void;
  onPreview?: (theme: KMDeckTheme) => void;
  onRestoreOriginal?: () => void;
  currentTheme?: KMDeckTheme;
}

const ColorCustomizationModal: React.FC<ColorCustomizationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onPreview,
  onRestoreOriginal,
  currentTheme
}) => {
  const toast = useToast();

  const [theme, setTheme] = useState<KMDeckTheme>(defaultTheme);

  // Initialize theme when modal opens or currentTheme changes
  useEffect(() => {
    if (isOpen) {
      setTheme(currentTheme || defaultTheme);
    }
  }, [isOpen, currentTheme]);

  const handleInputChange = (field: keyof KMDeckTheme, value: string | boolean) => {
    const newTheme = { ...theme, [field]: value };
    setTheme(newTheme);
    
    // Preview changes in real-time
    if (onPreview) {
      onPreview(newTheme);
    }
  };

  const [activeColorPicker, setActiveColorPicker] = useState<string | null>(null);

  const handleColorPickerFocus = (field: string) => {
    // If another color picker is active, blur it first
    if (activeColorPicker && activeColorPicker !== field) {
      const otherPicker = document.querySelector(`input[data-color-field="${activeColorPicker}"]`) as HTMLInputElement;
      if (otherPicker) {
        otherPicker.blur();
      }
    }
    setActiveColorPicker(field);
  };

  const handleColorPickerBlur = () => {
    setActiveColorPicker(null);
  };

  const handleGlowDisableChange = (isDisabled: boolean) => {
    const newTheme = { ...theme, glowDisabled: isDisabled };
    setTheme(newTheme);
    
    // Preview changes in real-time
    if (onPreview) {
      onPreview(newTheme);
    }
  };

  const handleSave = () => {
    onSave(theme);
    onClose();
  };


  const handleRestoreOriginal = () => {
    if (onRestoreOriginal) {
      onRestoreOriginal();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex={1400}
      pointerEvents="none"
    >
      <Box
        position="fixed"
        left="20px"
        top="50%"
        transform="translateY(-50%)"
        bg="gray.800"
        maxW="md"
        w="400px"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.600"
        boxShadow="xl"
        pointerEvents="auto"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          p={4}
          borderBottom="1px solid"
          borderColor="gray.600"
        >
          <Text color="gray.100" fontSize="lg" fontWeight="semibold">
            Customize Colors
          </Text>
          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            color="gray.300"
            _hover={{ bg: "gray.700" }}
            onClick={onClose}
          />
        </Box>
        
        <Box p={6}>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                Page Background
              </FormLabel>
              <HStack spacing={3}>
                <Input
                  type="color"
                  value={theme.pageBackground}
                  onChange={(e) => handleInputChange('pageBackground', e.target.value)}
                  onFocus={() => handleColorPickerFocus('pageBackground')}
                  onBlur={handleColorPickerBlur}
                  data-color-field="pageBackground"
                  w="60px"
                  h="40px"
                  p={1}
                  bg="gray.700"
                  borderColor="gray.600"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
                <Input
                  value={theme.pageBackground}
                  onChange={(e) => handleInputChange('pageBackground', e.target.value)}
                  placeholder="Page background (hex)"
                  bg="gray.700"
                  borderColor="gray.600"
                  color="gray.100"
                  fontSize="sm"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                Button Edges
              </FormLabel>
              <HStack spacing={3}>
                <Input
                  type="color"
                  value={theme.buttonEdges}
                  onChange={(e) => handleInputChange('buttonEdges', e.target.value)}
                  onFocus={() => handleColorPickerFocus('buttonEdges')}
                  onBlur={handleColorPickerBlur}
                  data-color-field="buttonEdges"
                  w="60px"
                  h="40px"
                  p={1}
                  bg="gray.700"
                  borderColor="gray.600"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
                <Input
                  value={theme.buttonEdges}
                  onChange={(e) => handleInputChange('buttonEdges', e.target.value)}
                  placeholder="Button edges (hex)"
                  bg="gray.700"
                  borderColor="gray.600"
                  color="gray.100"
                  fontSize="sm"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                Empty Button Background
              </FormLabel>
              <HStack spacing={3}>
                <Input
                  type="color"
                  value={theme.emptyButtonBackground}
                  onChange={(e) => handleInputChange('emptyButtonBackground', e.target.value)}
                  onFocus={() => handleColorPickerFocus('emptyButtonBackground')}
                  onBlur={handleColorPickerBlur}
                  data-color-field="emptyButtonBackground"
                  w="60px"
                  h="40px"
                  p={1}
                  bg="gray.700"
                  borderColor="gray.600"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
                <Input
                  value={theme.emptyButtonBackground}
                  onChange={(e) => handleInputChange('emptyButtonBackground', e.target.value)}
                  placeholder="Empty button background (hex)"
                  bg="gray.700"
                  borderColor="gray.600"
                  color="gray.100"
                  fontSize="sm"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                Glow Effect
              </FormLabel>
              <HStack spacing={3}>
                <Input
                  type="color"
                  value={theme.glowEffect}
                  onChange={(e) => handleInputChange('glowEffect', e.target.value)}
                  onFocus={() => handleColorPickerFocus('glowEffect')}
                  onBlur={handleColorPickerBlur}
                  data-color-field="glowEffect"
                  w="60px"
                  h="40px"
                  p={1}
                  bg="gray.700"
                  borderColor="gray.600"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
                <Input
                  value={theme.glowEffect}
                  onChange={(e) => handleInputChange('glowEffect', e.target.value)}
                  placeholder="Glow effect (hex)"
                  bg="gray.700"
                  borderColor="gray.600"
                  color="gray.100"
                  fontSize="sm"
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                />
              </HStack>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={theme.glowDisabled || false}
                onChange={(e) => handleGlowDisableChange(e.target.checked)}
                colorScheme="blue"
                color="gray.100"
              >
                <Text color="gray.200" fontSize="sm">
                  Disable glow effect
                </Text>
              </Checkbox>
            </FormControl>

            <HStack spacing={3} pt={2}>
              <Button
                onClick={handleRestoreOriginal}
                variant="outline"
                colorScheme="red"
                color="red.300"
                borderColor="red.500"
                _hover={{ bg: "red.900", borderColor: "red.400" }}
                flex={1}
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                colorScheme="blue"
                bg="blue.500"
                _hover={{ bg: "blue.600" }}
                _active={{ bg: "blue.700" }}
                flex={1}
              >
                Save Colors
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ColorCustomizationModal;
